<div class="recordings-page-container" [class.p-3]="!embedded" [class.embedded]="embedded">
	<mat-card [class.p-4]="!embedded" class="h-100">
		@if (!embedded) {
		<mat-card-header>
			<mat-card-title class="d-flex justify-content-between align-items-center w-100">
				<app-nav-map></app-nav-map>
				@if (loading) {
				<mat-spinner diameter="16" class="mt-2 ms-2"></mat-spinner>
				}
			</mat-card-title>
		</mat-card-header>

		<mat-divider class="w-75 mb-3"></mat-divider>
		}

		<div class="d-flex flex-wrap align-items-center filters pb-2">
			<mat-form-field class="me-3">
				<mat-label>{{ 'RECORDINGS.MEDIUM' | translate }}</mat-label>
				<mat-select [(ngModel)]="mediumId" (selectionChange)="load(); loadAccuracy()">
					<mat-option [value]="null">{{ 'RECORDINGS.ALL_MEDIUMS' | translate }}</mat-option>
					@for (medium of mediums; track medium.id) {
					<mat-option [value]="medium.id">{{ medium.name }}</mat-option>
					}
				</mat-select>
			</mat-form-field>
			<mat-form-field class="me-3 date-field">
				<mat-label>{{ 'RECORDINGS.FROM' | translate }}</mat-label>
				<input matInput [matDatepicker]="fromPicker" [(ngModel)]="from" (dateChange)="load()" />
				<mat-datepicker-toggle matIconSuffix [for]="fromPicker"></mat-datepicker-toggle>
				<mat-datepicker #fromPicker></mat-datepicker>
			</mat-form-field>
			<mat-form-field class="me-3 date-field">
				<mat-label>{{ 'RECORDINGS.TO' | translate }}</mat-label>
				<input matInput [matDatepicker]="toPicker" [(ngModel)]="to" (dateChange)="load()" />
				<mat-datepicker-toggle matIconSuffix [for]="toPicker"></mat-datepicker-toggle>
				<mat-datepicker #toPicker></mat-datepicker>
			</mat-form-field>
			<button mat-stroked-button (click)="load()" [disabled]="loading">
				<mat-icon>refresh</mat-icon>
				{{ 'RECORDINGS.REFRESH' | translate }}
			</button>
		</div>

		@if (accuracy && accuracy.reviewedEditions > 0) {
		<div class="accuracy d-flex flex-wrap align-items-center">
			<span class="accuracy-title">{{ 'RECORDINGS.ACCURACY' | translate }}</span>
			<span>{{ 'RECORDINGS.ITEM_RECALL' | translate }}: <b>{{ percent(accuracy.itemRecall) }}</b> <span class="muted">({{ accuracy.caughtItems }}/{{ accuracy.realItems }})</span></span>
			<span>{{ 'RECORDINGS.EXACT_BOUNDARIES' | translate }}: <b>{{ percent(accuracy.exactBoundaries) }}</b></span>
			<span class="muted">{{ 'RECORDINGS.REVIEWED_EDITIONS' | translate }}: {{ accuracy.reviewedEditions }} · {{ 'RECORDINGS.MERGED' | translate }}: {{ accuracy.merged }} · {{ 'RECORDINGS.MISSING' | translate }}: {{ accuracy.missing }} · {{ 'RECORDINGS.EXTRA' | translate }}: {{ accuracy.extra }}</span>
		</div>
		}

		@if (!loading && recordings.length === 0) {
		<p class="text-center py-4">{{ 'RECORDINGS.EMPTY' | translate }}</p>
		}

		@if (recordings.length > 0) {
		<div class="table-wrapper">
			<table class="recordings-table">
				<thead>
					<tr>
						<th>{{ 'RECORDINGS.DATE' | translate }}</th>
						<th>{{ 'RECORDINGS.MEDIUM' | translate }}</th>
						<th>{{ 'RECORDINGS.SLOT' | translate }}</th>
						<th>{{ 'RECORDINGS.RECORDED' | translate }}</th>
						<th>{{ 'RECORDINGS.STATUS' | translate }}</th>
						<th>{{ 'RECORDINGS.ITEMS' | translate }}</th>
						<th>{{ 'RECORDINGS.TRANSCRIPT' | translate }}</th>
						<th>{{ 'RECORDINGS.NOTE' | translate }}</th>
						<th></th>
					</tr>
				</thead>
				<tbody>
					@for (recording of recordings; track recording.id) {
					<tr [class.selected]="playing?.id === recording.id">
						<td>{{ formatDay(recording.scheduledStart) }}</td>
						<td class="medium">
							<span class="medium-name" matTooltip="{{ mediumName(recording) }}">{{ mediumName(recording) }}</span>
							@if (recording.source === 'UPLOAD') {
							<span class="uploaded-badge" matTooltip="{{ 'RECORDINGS.UPLOAD.BADGE_TOOLTIP' | translate }}">{{ 'RECORDINGS.UPLOAD.BADGE' | translate }}</span>
							}
						</td>
						<td>{{ formatTime(recording.scheduledStart) }}</td>
						<td class="nowrap">
							{{ formatDuration(recording.recordedDurationSeconds) }} / {{ formatDuration(recording.plannedDurationSeconds) }}
							@if (recording.parts > 1) {
							<span class="parts" matTooltip="{{ 'RECORDINGS.PARTS_TOOLTIP' | translate }}">({{ recording.parts }} {{ 'RECORDINGS.PARTS' | translate }})</span>
							}
						</td>
						<td>
							<span class="status-chip" [ngClass]="'status-' + recording.status">{{ 'RECORDINGS.STATUSES.' + recording.status | translate }}</span>
						</td>
						<td class="nowrap">
							@if (recording.segmentationStatus === 'RUNNING' || recording.status === 'SEGMENTING') {
							<span class="muted">{{ 'RECORDINGS.SEGMENTING' | translate }}</span>
							} @else if (recording.segmentationStatus === 'FAILED') {
							<a [routerLink]="['/admin/recordings', recording.id]" class="seg-failed">✖ {{ 'RECORDINGS.SEGMENTATION_FAILED' | translate }}</a>
							} @else if (recording.segmentationStatus === 'DONE') {
							<a [routerLink]="['/admin/recordings', recording.id]" class="review-link">
								{{ recording.itemCount }}
								@if (recording.reviewedAt) {
								<span class="reviewed">✔ {{ 'RECORDINGS.REVIEWED' | translate }}</span>
								} @else {
								<span class="to-review">{{ 'RECORDINGS.REVIEW' | translate }} →</span>
								}
							</a>
							} @else {
							<span class="muted">–</span>
							}
						</td>
						<td class="nowrap">
							@if (recording.transcriptionStatus === 'RUNNING') {
							<span class="muted">{{ 'RECORDINGS.TRANSCRIBING' | translate }}</span>
							} @else if (recording.transcriptionStatus === 'FAILED') {
							<a [routerLink]="['/admin/recordings', recording.id]" class="seg-failed">✖ {{ 'RECORDINGS.TRANSCRIPTION_FAILED' | translate }}</a>
							} @else if (recording.transcriptionStatus === 'DONE') {
							<span class="reviewed">✔</span>
							} @else {
							<span class="muted">–</span>
							}
						</td>
						<td class="note" matTooltip="{{ recording.failureReason }}">{{ recording.failureReason }}</td>
						<td class="nowrap actions">
							@if (recording.videoUrl) {
							<button mat-icon-button (click)="play(recording)" matTooltip="{{ 'RECORDINGS.PLAY' | translate }}">
								<mat-icon>{{ playing?.id === recording.id ? 'close' : 'play_arrow' }}</mat-icon>
							</button>
							<a mat-icon-button [href]="recording.videoUrl" target="_blank" rel="noopener" matTooltip="{{ 'RECORDINGS.OPEN' | translate }} {{ formatSize(recording.fileSizeBytes) }}">
								<mat-icon>open_in_new</mat-icon>
							</a>
							}
							<button
								mat-icon-button
								(click)="openEditDialog(recording)"
								[disabled]="isInFlight(recording)"
								matTooltip="{{ (isInFlight(recording) ? 'RECORDINGS.EDIT_DISABLED' : 'RECORDINGS.EDIT') | translate }}">
								<mat-icon>edit</mat-icon>
							</button>
							<button
								mat-icon-button
								(click)="delete(recording)"
								[disabled]="isInFlight(recording)"
								matTooltip="{{ (isInFlight(recording) ? 'RECORDINGS.DELETE_DISABLED' : 'RECORDINGS.DELETE') | translate }}">
								<mat-icon>delete</mat-icon>
							</button>
						</td>
					</tr>
					@if (playing?.id === recording.id && recording.videoUrl) {
					<tr class="player-row">
						<td colspan="9">
							<video [src]="recording.videoUrl" controls preload="metadata" class="player"></video>
						</td>
					</tr>
					}
					}
				</tbody>
			</table>
		</div>
		<p class="total pt-2">{{ 'RECORDINGS.TOTAL' | translate }}: {{ totalElements }}</p>
		}
	</mat-card>
</div>
